import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { EDITABLE_JOB_STATUSES, type JobDetail } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Card } from '../../../../components/Card';
import { createUserPreferenceStore } from '../../../../lib/preferences';
import { useCanEdit, useCurrentSession } from '../../../../stores/session';
import { MatrixTable } from '../builder/MatrixTable';
import { MATRIX_NO_ROUNDS_NOTE, useMatrixSection } from '../builder/BuilderSections';

/**
 * Owner 2026-11-01 (DECISIONS-LOG "Owner feedback…" Q2, Q3 a): the job builder's scoring matrix at the top of each Interview
 * step, editable here as in the builder (the same save), and folding away. It is the panel's guide: each panelist still
 * gives one overall mark (ADR 0077). Folded or open is remembered per person.
 */

const FOLDED_KEY = 'jobs.interview-matrix.folded';

export function InterviewMatrix({ job }: { readonly job: JobDetail }): ReactNode {
  const userId = useCurrentSession()?.id ?? 'anonymous';
  const store = useMemo(() => createUserPreferenceStore(userId), [userId]);
  const [isFolded, setIsFolded] = useState(() => store.get<unknown>(FOLDED_KEY, false) === true);
  const section = useMatrixSection(job);
  const isReadOnly = !useCanEdit('job_config') || !EDITABLE_JOB_STATUSES.includes(job.status);
  const named = section.rows.filter((row) => row.label.trim() !== '').length;
  const Fold = isFolded ? ChevronRight : ChevronDown;

  function toggle(): void {
    setIsFolded(!isFolded);
    store.set(FOLDED_KEY, !isFolded);
  }

  return (
    <Card
      title="Scoring matrix"
      meta={named === 0 ? 'What the panel looks for. Nothing written yet' : `What the panel looks for · ${named} ${named === 1 ? 'quality' : 'qualities'}`}
      action={
        <Button variant="ghost" icon={Fold} aria-expanded={!isFolded} onClick={toggle}>
          {isFolded ? 'Show' : 'Hide'}
        </Button>
      }
    >
      {!isFolded && <MatrixTable rows={section.rows} roundCount={0} onCommit={section.commit} onRowsChange={section.setRows} isReadOnly={isReadOnly} noRoundsNote={MATRIX_NO_ROUNDS_NOTE} />}
    </Card>
  );
}
